import React, { useEffect, useState } from "react";
import CusPageHeader from "../../../components/Molecules/header";
import { useNavigate, useParams } from "react-router-dom";
import {
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  Form,
  message,
  Row,
  Typography,
  Upload,
  Space,
  notification,
} from "antd";
import Page from "../../../components/Molecules/page";
import styled from "@emotion/styled";
import { UploadDoc } from "../../../components/Organisms/uploads/upload-doc";
import {
  useFetchSubCategoriesAndProductGroup,
  useTaskCompliance,
  useUpdateTaskCompliance,
} from "./api";
import { useDebounce } from "../../../utils";
import { useProductDetail } from "../ProjectPage/api";
import { useHttp } from "../../../utils/http";
import { getDemoFile } from "pages/Reviews/constants";
import { StaticDataContext } from "context/static-data-context";
import DocIcon from "components/Atoms/images/file-icon/doc_icon.png";
import PdfIcon from "components/Atoms/images/file-icon/pdf_icon.png";
import XlsIcon from "components/Atoms/images/file-icon/xls_icon.png";
import { ReactComponent as LightIcon } from "components/Atoms/images/light.svg";
import { FileImageOutlined } from "@ant-design/icons";
import { MainLayout } from "components/Organisms/layouts";
import { EditProductModal } from "../components/edit-product-modal";
import { useEditProduct } from "../api";
import * as _ from "lodash";

export function UploadDocument() {
  let navigate = useNavigate();

  const { ageGrade, serviceRequired } = React.useContext(StaticDataContext);
  const [testPlan, setTestPlan] = React.useState<any[]>([]);
  const [form] = Form.useForm();
  const { taskId, id } = useParams();
  const {
    mutate: editProductMutation,
    isLoading: isUpdating,
    isError,
    isSuccess,
  } = useEditProduct(id as string);
  const [param, setParam] = useState({ taskId, id });
  const [remarkDisabled, setRemarkDisabled] = useState(false);
  const [uploadDisabled, setUploadDisabled] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [editModal, setEditModal] = useState({
    open: false,
    initialValues: {},
  });
  const client = useHttp();
  const { isLoading, error, data: complianceData, retry } = useTaskCompliance(
    useDebounce(param, 200)
  );
  const { data: productDetails, retry: productReload } = useProductDetail(
    useDebounce(param, 200)
  );

  const [
    productCategories,
    subCategories,
    productGroups,
    exportMarkets,
  ] = useFetchSubCategoriesAndProductGroup(
    productDetails?.category ?? "",
    productDetails?.subcategory ?? ""
  );

  const { mutate, isLoading: isUpdateLoading } = useUpdateTaskCompliance();

  const handleNewData = async () => {
    const product_image = await Promise.all(
      complianceData?.productImages.map(async (item: any) => {
        const data = await client(`files/${item.uuid}`, {});
        return {
          name: item.fileName,
          uuid: item.uuid,
          url: data.url,
          status: "done",
        };
      })
    );

    const packaging_image = await Promise.all(
      complianceData?.packagingImages.map(async (item: any) => {
        const data = await client(`files/${item.uuid}`, {});
        return {
          name: item.fileName,
          uuid: item.uuid,
          url: data.url,
          status: "done",
        };
      })
    );

    const test_report = await Promise.all(
      complianceData?.testReport.map(async (item: any) => {
        const data = await client(`files/${item.uuid}`, {});
        return {
          name: item.fileName,
          uuid: item.uuid,
          url: data.url,
          status: "done",
        };
      })
    );

    const circuit_diagram = await Promise.all(
      complianceData?.circuitDiagram.map(async (item: any) => {
        const data = await client(`files/${item.uuid}`, {});
        return {
          name: item.fileName,
          uuid: item.uuid,
          url: data.url,
          status: "done",
        };
      })
    );

    const instruction_manual = await Promise.all(
      complianceData?.instructionManual.map(async (item: any) => {
        const data = await client(`files/${item.uuid}`, {});
        return {
          name: item.fileName,
          uuid: item.uuid,
          url: data.url,
          status: "done",
        };
      })
    );

    const safety_data_sheet = await Promise.all(
      complianceData?.safetyDataSheet.map(async (item: any) => {
        const data = await client(`files/${item.uuid}`, {});
        return {
          name: item.fileName,
          uuid: item.uuid,
          url: data.url,
          status: "done",
        };
      })
    );

    const ingredient_list = await Promise.all(
      complianceData?.ingredientList.map(async (item: any) => {
        const data = await client(`files/${item.uuid}`, {});
        return {
          name: item.fileName,
          uuid: item.uuid,
          url: data.url,
          status: "done",
        };
      })
    );

    const declaration_letter = await Promise.all(
      complianceData?.declarationLetter.map(async (item: any) => {
        const data = await client(`files/${item.uuid}`, {});
        return {
          name: item.fileName,
          uuid: item.uuid,
          url: data.url,
          status: "done",
        };
      })
    );

    const conformity_certificate = await Promise.all(
      (complianceData?.conformityCertificate ?? []).map(async (item: any) => {
        const data = await client(`files/${item.uuid}`, {});
        return {
          docType: item.type || item.docType,
          name: item.fileName,
          uuid: item.uuid,
          url: data.url,
          status: "done",
        };
      })
    );

    const test_plan = await Promise.all(
      complianceData?.testPlan.map(async (item: any) => {
        const data = await client(`files/${item.uuid}`, {});
        return {
          docType: item.type || item.docType,
          name: item.fileName,
          uuid: item.uuid,
          url: data.url,
          status: "done",
        };
      })
    );
    const materials_used = await Promise.all(
      (complianceData?.materialsUsedImages || []).map(async (item: any) => {
        const data = await client(`files/${item.uuid}`, {});
        return {
          name: item.fileName,
          uuid: item.uuid,
          url: data.url,
          status: "done",
        };
      })
    );
    setTestPlan(test_plan || []);
    await form.setFieldsValue({
      packaging_image,
      product_image,
      test_report,
      circuit_diagram,
      instruction_manual,
      safety_data_sheet,
      ingredient_list,
      declaration_letter,
      conformity_certificate,
      materials_used,
      test_plan: test_plan,
      ...complianceData,
    });
  };

  const isRequired = React.useMemo(() => {
    if (!productDetails) return true;
    return (
      productDetails.status !== "rejected" &&
      productDetails.status !== "completed"
    );
  }, [productDetails]);

  const haveRejected = React.useCallback(
    (key): Partial<{ isReupload: boolean; remark: string } | null> => {
      if (
        productDetails?.status !== "rejected" &&
        productDetails?.status !== "completed"
      )
        return null;
      if (!complianceData) return null;
      const keys = Object.keys(complianceData.reviewer).filter((_key) =>
        _key.includes(key)
      );
      if (!!keys.length) {
        const test = keys.reduce((acc, current) => {
          const key = current.includes(`isReupload`) ? "isReupload" : "remark";
          return {
            ...acc,
            [key]: complianceData.reviewer[current],
          };
        }, {});
        return test;
      }
      return null;
    },
    [productDetails, complianceData]
  );

  useEffect(() => {
    if (productDetails) {
      if (
        productDetails.status !== "missing_documents" &&
        productDetails.status !== "rejected"
      ) {
        setRemarkDisabled(true);
        setUploadDisabled(true);
        setSaveDisabled(true);
      }
    }
  }, [productDetails]);

  useEffect(() => {
    if (complianceData && productDetails) {
      handleNewData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complianceData, productDetails]);
  const onSubmit = async (isDraft: boolean) => {
    try {
      if (!isDraft) {
        await form.validateFields();
      }
    } catch (error) {
      setTimeout(() => {
        document
          .getElementsByClassName("ant-form-item-has-error")[0]
          .scrollIntoView({
            behavior: "smooth",
            block: "end",
            inline: "nearest",
          });
      }, 250);

      return;
    }

    const formData = form.getFieldsValue();

    let tempFile: any[] = [];

    if (formData?.e_signature) {
      tempFile.push(
        ...formData?.e_signature.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType || item.type,
          fileName: item.name || item.fileName,
          size: item.size,
        }))
      );
    }

    if (formData?.product_image) {
      tempFile.push(
        ...formData?.product_image.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType || item.type,
          fileName: item.name || item.fileName,
          size: item.size,
        }))
      );
    }

    if (formData?.packaging_image) {
      tempFile.push(
        ...formData?.packaging_image.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType,
          fileName: item.name,
          size: item.size,
        }))
      );
    }

    if (formData?.test_report) {
      tempFile.push(
        ...formData?.test_report.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType,
          fileName: item.name,
          size: item.size,
        }))
      );
    }

    if (formData?.circuit_diagram) {
      tempFile.push(
        ...formData?.circuit_diagram.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType,
          fileName: item.name,
          size: item.size,
        }))
      );
    }

    if (formData?.instruction_manual) {
      tempFile.push(
        ...formData?.instruction_manual.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType,
          fileName: item.name,
          size: item.size,
        }))
      );
    }

    if (formData?.safety_data_sheet) {
      tempFile.push(
        ...formData?.safety_data_sheet.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType,
          fileName: item.name,
          size: item.size,
        }))
      );
    }

    if (formData?.ingredient_list) {
      tempFile.push(
        ...formData?.ingredient_list.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType,
          fileName: item.name,
          size: item.size,
        }))
      );
    }

    if (formData?.declaration_letter) {
      tempFile.push(
        ...formData?.declaration_letter.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType,
          fileName: item.name,
          size: item.size,
        }))
      );
    }

    if (formData?.conformity_certificate) {
      tempFile.push(
        ...formData?.conformity_certificate.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType,
          fileName: item.name,
          size: item.size,
        }))
      );
    }
    if (formData?.materials_used) {
      tempFile.push(
        ...formData?.materials_used.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType,
          fileName: item.name,
          size: item.size,
        }))
      );
    }

    if (testPlan) {
      tempFile.push(
        ...testPlan.map((item: any) => ({
          uuid: item.uuid,
          type: item.docType,
          fileName: item.name,
          size: item.size,
        }))
      );
    }

    for (let i = 0; i < tempFile.length; i++) {
      if (!tempFile[i].uuid) {
        const hide = message.loading(
          "Some files are still uploading, please wait..",
          0
        );
        // Dismiss manually and asynchronously
        setTimeout(hide, 2500);

        return;
      }
    }

    const submitData = {
      files: tempFile,
      conformityCertificate: {},
      productImageRemark: formData?.productImageRemark,
      packagingImageRemark: formData?.packagingImageRemark,
      testReportRemark: formData?.testReportRemark,
      briefDescription: formData?.briefDescription,
      materialsUsed: formData?.materialsUsed,
      circuitDiagramRemark: formData?.circuitDiagramRemark,
      instructionManualRemark: formData?.instructionManualRemark,
      safetyDataSheetRemark: formData?.safetyDataSheetRemark,
      ingredientListRemark: formData?.ingredientListRemark,
      declarationLetterRemark: formData?.declarationLetterRemark,
      conformityCertificateRemark: formData?.conformityCertificateRemark,
      status: "waiting_for_review",
      isDrafted: isDraft,
      is_esign_flow: false,
      testLines: complianceData.testLines,
    };

    await mutate({ params: submitData, taskId: taskId || "" });
    navigate(-1);
  };

  const initialValues = React.useMemo(() => {
    const initialValues = {
      ...productDetails,
      categoryId: (productCategories || []).find(
        ({ name }) => name === productDetails?.category
      )?.id,
      subcategoryId: (subCategories || []).find(
        ({ name }) => name === productDetails?.subcategory
      )?.id,
      productGroupId: (productGroups || []).find(
        ({ name }) => name === productDetails?.productGroup
      )?.id,
      supplierTags: productDetails?.supplierTags?.map((value: any) => ({
        value,
        label: value,
      })),

      exportMarketId: (exportMarkets || []).find(
        ({ name }) => name === productDetails?.exportMarket
      )?.id,
    };
    return initialValues;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productDetails, subCategories, productGroups]);

  const onEditTestLine = () => {
    setEditModal({
      open: true,
      initialValues,
    });
  };

  const isAutoAssigned = React.useMemo(() => {
    if (!!complianceData?.testPlan?.length) {
      return false;
    }
    const mockedFiles = getDemoFile(productDetails);
    return !!mockedFiles[0];
  }, [productDetails, complianceData]);

  const handleEditProduct = async (values: Record<string, any>) => {
    try {
      await editProductMutation({
        ...values,
        supplierTags: (values.supplierTags || []).map(
          ({ value }: any) => value
        ),
      } as any);

      productReload();
      setEditModal({ ...editModal, open: false });
    } catch (error) {
      notification.error((error as any).message);
    }
  };

  return (
    <MainLayout>
      <CusPageHeader
        title={
          <div>
            Compliance Check <Divider type={`vertical`} />{" "}
            <span style={{ color: "#777777" }}>Requirement Detail</span>
          </div>
        }
        onBack={() => navigate(-1)}
      />
      <Page>
        {complianceData && (
          <>
            <Form name="validate_other" form={form} scrollToFirstError>
              <Card>
                <InfoCard>
                  <InfoCardHeader>
                    <h2>Product Number: {productDetails?.productNumber}</h2>
                    <Button
                      hidden={productDetails?.status === "completed"}
                      onClick={onEditTestLine}
                      type="link"
                    >
                      Edit
                    </Button>
                  </InfoCardHeader>
                  <div className={"info-card-body"}>
                    <Row wrap={false}>
                      <Col flex="none">
                        <HeadTitle1>Product Name:</HeadTitle1>
                        <Typography.Text>
                          {productDetails?.productName}
                        </Typography.Text>
                        <VerticalLine type={`vertical`} />
                      </Col>
                      <Col flex="none">
                        <HeadTitle1>Age Grade:</HeadTitle1>
                        <Typography.Text>
                          {ageGrade.find(
                            ({ value }) => value === productDetails?.ageGrade
                          )?.label ?? "N/A"}
                        </Typography.Text>
                      </Col>
                    </Row>
                    <Row wrap={false}>
                      <Col flex="none">
                        <HeadTitle1>Export Market: </HeadTitle1>
                        <Typography.Text>
                          {productDetails?.exportMarket}
                        </Typography.Text>
                        <VerticalLine type={`vertical`} />
                      </Col>
                      <Col flex="none">
                        <HeadTitle1>Category: </HeadTitle1>
                        <Typography.Text>
                          {productDetails?.category}
                        </Typography.Text>
                        <VerticalLine type={`vertical`} />
                      </Col>
                      <Col flex="none">
                        <HeadTitle1>Sub-category: </HeadTitle1>
                        <Typography.Text>
                          {productDetails?.subcategory}
                        </Typography.Text>
                        <VerticalLine type={`vertical`} />
                      </Col>
                      <Col flex="none">
                        <HeadTitle1>Service Required: </HeadTitle1>
                        <Typography.Text>
                          {serviceRequired.find(
                            ({ value }) =>
                              value === productDetails?.serviceRequired
                          )?.label ?? "N/A"}
                        </Typography.Text>

                        <VerticalLine type={`vertical`} />
                      </Col>
                      <Col flex="none">
                        <HeadTitle1>ECD: </HeadTitle1>
                        <Typography.Text>
                          {productDetails?.estimatedCompletionDate || "N/A"}
                        </Typography.Text>
                      </Col>
                    </Row>
                    <Row wrap={false}>
                      <Col flex="none">
                        <HeadTitle1>Product Description:</HeadTitle1>
                        <Typography.Text>
                          {productDetails?.productDescription}
                        </Typography.Text>
                      </Col>
                    </Row>
                  </div>
                </InfoCard>
                <InfoCard>
                  <h2>Test Plan</h2>
                  <StyledCardFullWidth>
                    <Row>
                      <Col span={24}>
                        {isAutoAssigned ? (
                          <Typography>
                            Enclosed herewith the general test plan for your
                            reference. Should you have any question, please feel
                            free to contact ICW compliance team at{" "}
                            <a href="mailto:info@icw.io">info@icw.io.</a>
                          </Typography>
                        ) : (
                          <Space align="center">
                            <LightIcon></LightIcon>
                            <Space direction="vertical" size={2}>
                              <Typography className="title">
                                Pending assign test plan
                              </Typography>
                              <Typography className="caption">
                                Reviewer will upload a test plan later. Before
                                that, please upload the compliance document as
                                much as possible.
                              </Typography>
                            </Space>
                          </Space>
                        )}
                      </Col>
                    </Row>
                    <Row>
                      <Col span={24}>
                        <Upload
                          fileList={testPlan}
                          itemRender={renderFileItem}
                          listType="picture"
                        ></Upload>
                      </Col>
                    </Row>
                  </StyledCardFullWidth>
                </InfoCard>
                <InfoCard>
                  <UploadDoc
                    title={`1. Business Licensing, Licensing Agreement`}
                    type={`drag`}
                    name={`product_image`}
                    remarkName={`productImageRemark`}
                    required={isRequired}
                    danger={haveRejected("ProductImage")?.isReupload}
                    reviewerRemark={haveRejected("ProductImage")?.remark}
                    remarkDisabled={remarkDisabled}
                    uploadDisabled={uploadDisabled}
                    saveDisabled={saveDisabled}
                    rules={[
                      {
                        validator: (rule, value, callback) => {
                          const { field } = rule as any;
                          if (field === "product_image") {
                            return value.length
                              ? Promise.resolve()
                              : Promise.reject("The images was required");
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  />
                </InfoCard>
                <InfoCard>
                  <UploadDoc
                    title={`2. Product and Packaging (up to 10 images)`}
                    type={`drag`}
                    name={`packaging_image`}
                    required={isRequired}
                    danger={haveRejected("PackagingImage")?.isReupload}
                    reviewerRemark={haveRejected("PackagingImage")?.remark}
                    remarkName={`packagingImageRemark`}
                    remarkDisabled={remarkDisabled}
                    uploadDisabled={uploadDisabled}
                    saveDisabled={saveDisabled}
                    rules={[
                      {
                        validator: (rule, value, callback) => {
                          const { field } = rule as any;
                          if (field === "packaging_image") {
                            return value.length
                              ? Promise.resolve()
                              : Promise.reject("The images was required");
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  />
                </InfoCard>
                <InfoCard>
                  <UploadDoc
                    title={`3. Product Testing Report / EU Examination Certificate `}
                    type={`click`}
                    name={`test_report`}
                    hints={true}
                    danger={haveRejected("TestReport")?.isReupload}
                    reviewerRemark={haveRejected("TestReport")?.remark}
                    remarkName={`testReportRemark`}
                    remarkDisabled={remarkDisabled}
                    uploadDisabled={uploadDisabled}
                    saveDisabled={saveDisabled}
                  />
                </InfoCard>
                <InfoCard>
                  <UploadDoc
                    title={`4. Product functions & features (e.g. name, model number, feature, special function, etc)`}
                    remarkName={`briefDescription`}
                    danger={haveRejected("BriefDescription")?.isReupload}
                    reviewerRemark={haveRejected("BriefDescription")?.remark}
                    remarkDisabled={remarkDisabled}
                    uploadDisabled={uploadDisabled}
                    saveDisabled={saveDisabled}
                  />
                </InfoCard>

                <InfoCard>
                  <UploadDoc
                    title={`5. List of components and materials used (Bill of materials / Bill of substances)`}
                    remarkName={`materialsUsed`}
                    type={`click`}
                    name="materials_used"
                    danger={haveRejected("MaterialsUsed")?.isReupload}
                    reviewerRemark={haveRejected("MaterialsUsed")?.remark}
                    remarkDisabled={remarkDisabled}
                    uploadDisabled={uploadDisabled}
                    saveDisabled={saveDisabled}
                  />
                </InfoCard>

                <InfoCard>
                  <UploadDoc
                    title={`6. Circuit diagram `}
                    type={`click`}
                    name={`circuit_diagram`}
                    remarkName={`circuitDiagramRemark`}
                    danger={haveRejected("CircuitDiagram")?.isReupload}
                    reviewerRemark={haveRejected("CircuitDiagram")?.remark}
                    remarkDisabled={remarkDisabled}
                    uploadDisabled={uploadDisabled}
                    saveDisabled={saveDisabled}
                  />
                </InfoCard>

                <InfoCard>
                  <UploadDoc
                    title={`7. Instruction manual`}
                    type={`click`}
                    name={`instruction_manual`}
                    remarkName={`instructionManualRemark`}
                    danger={haveRejected("InstructionManual")?.isReupload}
                    reviewerRemark={haveRejected("InstructionManual")?.remark}
                    remarkDisabled={remarkDisabled}
                    uploadDisabled={uploadDisabled}
                    saveDisabled={saveDisabled}
                  />
                </InfoCard>

                <InfoCard>
                  <UploadDoc
                    title={`8. Safety data sheet(s) for chemicals used`}
                    type={`click`}
                    name={`safety_data_sheet`}
                    remarkName={`safetyDataSheetRemark`}
                    danger={haveRejected("SafetyDataSheet")?.isReupload}
                    reviewerRemark={haveRejected("SafetyDataSheet")?.remark}
                    remarkDisabled={remarkDisabled}
                    uploadDisabled={uploadDisabled}
                    saveDisabled={saveDisabled}
                  />
                </InfoCard>

                <InfoCard>
                  <UploadDoc
                    title={`9. Ingredient list or formulation`}
                    type={`click`}
                    name={`ingredient_list`}
                    remarkName={`ingredientListRemark`}
                    danger={haveRejected("IngredientList")?.isReupload}
                    reviewerRemark={haveRejected("IngredientList")?.remark}
                    remarkDisabled={remarkDisabled}
                    uploadDisabled={uploadDisabled}
                    saveDisabled={saveDisabled}
                  />
                </InfoCard>

                <InfoCard>
                  <UploadDoc
                    title={`10. Manufacturer Information and Production Date Code`}
                    type={`click`}
                    name={`declaration_letter`}
                    remarkName={`declarationLetterRemark`}
                    danger={haveRejected("DeclarationLetter")?.isReupload}
                    reviewerRemark={haveRejected("DeclarationLetter")?.remark}
                    remarkDisabled={remarkDisabled}
                    uploadDisabled={uploadDisabled}
                    saveDisabled={saveDisabled}
                  />
                </InfoCard>

                <InfoCard>
                  <UploadDoc
                    title={`11. Conformity Certificate`}
                    type={`click`}
                    name={`conformity_certificate`}
                    preferable={true}
                    remarkName={`conformityCertificateRemark`}
                    danger={haveRejected("ConformityCertificate")?.isReupload}
                    reviewerRemark={
                      haveRejected("ConformityCertificate")?.remark
                    }
                    remarkDisabled={remarkDisabled}
                    uploadDisabled={uploadDisabled}
                    saveDisabled={saveDisabled}
                  />
                </InfoCard>
              </Card>
              <Row
                justify="space-between"
                align="middle"
                style={{ margin: "40px 0" }}
              >
                <Col flex={"none"}>
                  <Button
                    disabled={saveDisabled}
                    size={`large`}
                    type={`primary`}
                    loading={isUpdateLoading}
                    onClick={() => onSubmit(false)}
                  >
                    Submit
                  </Button>
                </Col>
                <Col flex={"none"}>
                  <Button
                    disabled={saveDisabled}
                    size={`large`}
                    loading={isUpdateLoading}
                    onClick={() => onSubmit(true)}
                  >
                    Save draft
                  </Button>
                </Col>
              </Row>
            </Form>
          </>
        )}
      </Page>
      <EditProductModal
        open={editModal.open}
        initialValues={{ ...initialValues }}
        handleCloseModal={() => {
          setEditModal({ ...editModal, open: false });
        }}
        isUpdating={isUpdating}
        updateProduct={handleEditProduct}
      ></EditProductModal>
    </MainLayout>
  );
}

const InfoCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px;
  width: 100%;
  margin-bottom: 24px;
  background: #ffffff;
  border: 1px solid rgba(170, 170, 170, 0.3);
  border-radius: 16px;
  position: relative;

  .info-card-body {
    color: #777777;
  }
  .title {
    margin-top: 0px;
    margin-bottom: 0px;
  }
`;

const InfoCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const HeadTitle1 = styled.span`
  color: #777777;
  margin-right: 5px;
`;

const VerticalLine = styled(Divider)`
  border: 1px solid #e4e4e4;
  margin: 0px 10px 0px 10px !important;
`;

const StyledCardFullWidth = styled(Card)`
  width: 100%;
  background: #fafafa;
  .title {
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    letter-spacing: 0.002em;
    text-align: left;
    color: #232b3a;
  }
  .caption {
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: 0.005em;
    text-align: left;
    color: #777777;
  }
`;

const renderFileItem = (
  originNode: React.ReactElement,
  file: any,
  fileList: object[],
  actions: any
) => {
  const patternFileExtension = /\.([0-9a-z]+)(?:[#]|$)/i;
  const extension =
    (file.fileName
      ? file.fileName.match(patternFileExtension)
      : file.name.match(patternFileExtension)) || [];

  let icon = null;
  switch (extension[1]) {
    case "doc":
    case "docx":
      icon = DocIcon;
      break;
    case "pdf":
      icon = PdfIcon;
      break;
    case "xls":
    case "xlsx":
      icon = XlsIcon;
      break;
    default:
      icon = <FileImageOutlined style={{ color: "#F98001", fontSize: 22 }} />;
  }

  return (
    <Row align="middle">
      <Col flex={"none"}>
        <Avatar
          shape="square"
          size="small"
          style={{ margin: "16px 10px 5px 0px" }}
          src={icon}
        />
      </Col>
      <Col flex={`none`}>
        <div
          style={{ marginTop: 12, cursor: "pointer" }}
          onClick={() => actions.download()}
        >
          {file.name || file.fileName}
        </div>
      </Col>
    </Row>
  );
};
